import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { Button } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { TicketsRoutes, getPathForRoute } from "constants/tickets/TicketsRoutes";
import { EventPlace, trackEngagement } from "helpers/analytics";
import useModal from "hooks/use-modal";
import { TicketActionTypes, TicketPriority, TicketSilo, TicketStatus } from "interfaces/entities/ticket";
import { actions$ } from "store/action-stream";
import { selectAgentsAlphabetically, selectIsAdmin, selectIsAgent } from "store/entities/agents";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { IAction } from "store/helper";
import { TicketsViewActions, TicketsViewActionsNames } from "store/views/tickets/actions";
import { getSelectedTicketIds, getSilo } from "store/views/tickets/selectors";

import { Th } from "../tickets-styles";

import BulkAction from "./BulkAction";
import BulkDeleteModal from "./BulkDeleteModal";
import { EMPTY_PENDING_ACTIONS } from "./constants";
import { IPendingActions } from "./interfaces";

const bulkActions = {
  default: [
    TicketActionTypes.SetStatus,
    TicketActionTypes.SetPriority,
    TicketActionTypes.AssignTeam,
    TicketActionTypes.AssignAgent,
    TicketActionTypes.AddTag,
    TicketActionTypes.MoveToArchive,
    TicketActionTypes.MoveToSpam,
    TicketActionTypes.MoveToTrash,
  ],
  defaultArchive: [TicketActionTypes.Unarchive, TicketActionTypes.MoveToTrash],
  adminSpam: [TicketActionTypes.NotSpam, TicketActionTypes.DeleteForever],
  agentSpam: [TicketActionTypes.NotSpam],
  adminTrash: [TicketActionTypes.Restore, TicketActionTypes.DeleteForever],
  agentTrash: [TicketActionTypes.Restore],
};

const BulkActions: FC<{
  colSpan: number;
  onSpamModalRequest(type: "spam" | "unspam", requesterEmails: string[]): void;
}> = ({ colSpan, onSpamModalRequest }) => {
  const dispatch = useDispatch();
  const tags = useSelector(selectTagsAlphabetically);
  const teams = useSelector(selectTeamsAlphabetically);
  const agents = useSelector(selectAgentsAlphabetically);
  const isOwner = useSelector(selectIsAdmin);
  const isAgent = useSelector(selectIsAgent);
  const selectedIds = useSelector(getSelectedTicketIds);
  const silo = useSelector(getSilo);

  const [pendingActions, setPendingActions] = useState<IPendingActions>(EMPTY_PENDING_ACTIONS);
  const [isApplying, setIsApplying] = useState(false);

  const unsubscribeDone = useRef(null);
  const viewChangeUnsubscribe = useRef(null);
  const [deleteModalVisible, { show: showDeleteModal, close: closeDeleteModal }] = useModal(false);

  const location = useLocation();
  const isArchivePath = location.pathname === getPathForRoute(TicketsRoutes.Archive) || silo === TicketSilo.Archive;
  const isSpamPath = location.pathname === getPathForRoute(TicketsRoutes.Spam) || silo === TicketSilo.Spam;
  const isTrashPath = location.pathname === getPathForRoute(TicketsRoutes.Trash) || silo === TicketSilo.Trash;
  const isOtherPath = !isArchivePath && !isSpamPath && !isTrashPath;

  const hasPendingActions =
    pendingActions.status !== null ||
    pendingActions.tagIds !== null ||
    pendingActions.priority !== null ||
    pendingActions.teamId !== null ||
    pendingActions.agentId !== null;

  const isTicketEditable = !isArchivePath && !isSpamPath && !isTrashPath;

  const handleBulkActionsPerform = (): void => {
    setIsApplying(true);
    dispatch(
      TicketsViewActions.performBulkAction({
        selectedIds,
        pendingActions: {
          status: pendingActions.status && (pendingActions.status[0].key as TicketStatus),
          priority: pendingActions.priority && (parseInt(pendingActions.priority[0].key, 10) as TicketPriority),
          teamId: pendingActions.teamId && pendingActions.teamId[0].key,
          agentId: pendingActions.agentId && pendingActions.agentId[0].key,
          // @ts-expect-error TS(2531): Object is possibly 'null'.
          tagIds: pendingActions.tagIds.map(tag => tag.key),
        },
      })
    );
    trackEngagement("Bulk actions: Bulk update performed", EventPlace.Tickets, {
      ticketCount: selectedIds.length,
      statusChanged: pendingActions.status !== null,
      priorityChanged: pendingActions.priority !== null,
      teamChanged: pendingActions.teamId !== null,
      agentChanged: pendingActions.agentId !== null,
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      tagsAdded: pendingActions.tagIds.length > 0,
    });
  };

  useEffect(() => {
    // @ts-expect-error TS(2322)
    viewChangeUnsubscribe.current = pipe(
      actions$,
      filter((action: IAction) => action.type === TicketsViewActionsNames.SET_VIEW),
      subscribe(() => {
        setPendingActions(EMPTY_PENDING_ACTIONS);
      })
    );

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (viewChangeUnsubscribe.current) {
        // @ts-expect-error TS(2349): This expression is not callable.
        viewChangeUnsubscribe.current();
      }
    };
  }, []);

  useEffect(() => {
    if (isApplying) {
      // @ts-expect-error TS(2322)
      unsubscribeDone.current = pipe(
        actions$,
        filter((action: IAction) => action.type === TicketsViewActionsNames.PERFORM_BULK_ACTION_DONE),
        subscribe(() => {
          setIsApplying(false);
          setPendingActions(EMPTY_PENDING_ACTIONS);
        })
      );
    }

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (unsubscribeDone.current) {
        // @ts-expect-error TS(2349): This expression is not callable.
        unsubscribeDone.current();
        unsubscribeDone.current = null;
      }
    };
  }, [isApplying]);

  return (
    <Th colSpan={colSpan}>
      <Flex alignItems="center" flexWrap minHeight="14px" margin="-8px">
        {isOtherPath &&
          bulkActions.default.map(action => (
            <BulkAction
              key={action}
              actionType={action}
              pendingActions={pendingActions}
              setPendingActions={setPendingActions}
              selectedIds={selectedIds}
              agents={agents}
              teams={teams}
              tags={tags}
              onSpamModalRequest={onSpamModalRequest}
            />
          ))}
        {isArchivePath &&
          bulkActions.defaultArchive.map(action => (
            <BulkAction
              key={action}
              actionType={action}
              pendingActions={pendingActions}
              setPendingActions={setPendingActions}
              selectedIds={selectedIds}
            />
          ))}
        {isSpamPath &&
          isAgent &&
          bulkActions.agentSpam.map(action => (
            <BulkAction
              key={action}
              actionType={action}
              pendingActions={pendingActions}
              setPendingActions={setPendingActions}
              selectedIds={selectedIds}
              onSpamModalRequest={onSpamModalRequest}
            />
          ))}
        {isSpamPath &&
          isOwner &&
          bulkActions.adminSpam.map(action => (
            <BulkAction
              key={action}
              actionType={action}
              pendingActions={pendingActions}
              setPendingActions={setPendingActions}
              selectedIds={selectedIds}
              showDeleteModal={showDeleteModal}
              onSpamModalRequest={onSpamModalRequest}
            />
          ))}
        {isTrashPath &&
          isAgent &&
          bulkActions.agentTrash.map(action => (
            <BulkAction
              key={action}
              actionType={action}
              pendingActions={pendingActions}
              setPendingActions={setPendingActions}
              selectedIds={selectedIds}
            />
          ))}
        {isTrashPath &&
          isOwner &&
          bulkActions.adminTrash.map(action => (
            <BulkAction
              key={action}
              actionType={action}
              pendingActions={pendingActions}
              setPendingActions={setPendingActions}
              selectedIds={selectedIds}
              showDeleteModal={showDeleteModal}
            />
          ))}
        {isTicketEditable && hasPendingActions && selectedIds.length > 0 && (
          <Button size="xcompact" kind="secondary" disabled={isApplying} onClick={handleBulkActionsPerform}>
            {isApplying ? "Applying…" : "Apply"}
          </Button>
        )}
        {deleteModalVisible && <BulkDeleteModal onClose={closeDeleteModal} />}
      </Flex>
    </Th>
  );
};

export default BulkActions;
